/**
 * 風の洞察 (Kaze no Dōsatsu) Intelligence Dashboard
 *
 * Market intelligence overview connected to the Kaze engine.
 * Tabs: Overview, Narratives, Edges, Alerts, Briefing
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Chip,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  LinearProgress,
  IconButton,
  Tooltip,
  Divider,
  TextField,
  MenuItem,
  Button,
} from '@mui/material';
import {
  Air,
  Refresh,
  Circle,
  TrendingUp,
  TrendingDown,
  Remove,
  Radar,
  Warning,
  NotificationsActive,
  Article,
  Hub,
  PlayArrow,
  CheckCircle,
  ErrorOutline,
  Forum,
  ShowChart,
  TableChart,
  Assessment,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import kazeService, {
  KazeHealth,
  RealitySnapshot,
  NarrativesData,
  NarrativeState,
  EdgeCandidate,
  KazeAlert,
  IntelEvent,
  TomiBridgeStatus,
  LearningData,
  SectorConsensus,
  RotationSignal,
  TickerAnalysis,
  ChartAnalysisResult,
  YahooTop100Ticker,
} from '../services/kazeService';
import AgentChatPanel from '../components/AgentChatPanel';
import { createEquityDatafeed } from '../utils/tradingViewEquityDatafeed';

// ─── Helpers ────────────────────────────────────────────

/** Convert a UTC timestamp (without Z suffix) to Eastern Time display string */
function toEST(ts: string): string {
  if (!ts) return '';
  const utc = ts.endsWith('Z') || ts.includes('+') ? ts : ts + 'Z';
  return new Date(utc).toLocaleString('en-US', {
    timeZone: 'America/New_York',
    month: 'numeric',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  });
}

interface TabPanelProps {
  children: React.ReactNode;
  value: number;
  index: number;
}

function TabPanel({ children, value, index }: TabPanelProps) {
  return value === index ? <Box sx={{ pt: 3 }}>{children}</Box> : null;
}

function StatusDot({ ok }: { ok: boolean }) {
  return (
    <Circle
      sx={{
        fontSize: 12,
        color: ok ? '#4caf50' : '#f44336',
        mr: 1,
        animation: ok ? undefined : 'pulse 1.5s infinite',
        '@keyframes pulse': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.4 } },
      }}
    />
  );
}

function trendIcon(trend: string) {
  if (['rising', 'up', 'strengthening'].includes(trend)) return <TrendingUp sx={{ fontSize: 16, color: '#4caf50' }} />;
  if (['falling', 'down', 'weakening'].includes(trend)) return <TrendingDown sx={{ fontSize: 16, color: '#f44336' }} />;
  return <Remove sx={{ fontSize: 16, color: '#9e9e9e' }} />;
}

function scoreColor(score: number): string {
  if (score >= 0.8) return '#4caf50';
  if (score >= 0.6) return '#8bc34a';
  if (score >= 0.4) return '#ff9800';
  if (score >= 0.2) return '#ff5722';
  return '#f44336';
}

function tierLabel(tier: number): { label: string; color: 'error' | 'warning' | 'info' } {
  if (tier === 1) return { label: 'Tier 1 — Critical', color: 'error' };
  if (tier === 2) return { label: 'Tier 2 — Actionable', color: 'warning' };
  return { label: `Tier ${tier}`, color: 'info' };
}

// ─── Main Component ─────────────────────────────────────

export default function KazeDashboardPage() {
  const theme = useTheme();

  // State
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [health, setHealth] = useState<KazeHealth | null>(null);
  const [reality, setReality] = useState<RealitySnapshot | null>(null);
  const [narratives, setNarratives] = useState<NarrativesData | null>(null);
  const [edges, setEdges] = useState<EdgeCandidate[]>([]);
  const [alerts, setAlerts] = useState<KazeAlert[]>([]);
  const [tomiBridge, setTomiBridge] = useState<TomiBridgeStatus | null>(null);
  const [learning, setLearning] = useState<LearningData | null>(null);
  const [briefing, setBriefing] = useState<string | null>(null);
  const [events, setEvents] = useState<IntelEvent[]>([]);
  const [polling, setPolling] = useState(false);

  // New: Sectors, Screener, Chart Analysis state
  const [sectors, setSectors] = useState<SectorConsensus[]>([]);
  const [rotationSignals, setRotationSignals] = useState<RotationSignal[]>([]);
  const [screenerTickers, setScreenerTickers] = useState<TickerAnalysis[]>([]);
  const [screenerSectorFilter, setScreenerSectorFilter] = useState('');
  const [screenerKClassFilter, setScreenerKClassFilter] = useState('');
  const [top100, setTop100] = useState<YahooTop100Ticker[]>([]);
  const [chartTicker, setChartTicker] = useState('AAPL');
  const [chartAnalysis, setChartAnalysis] = useState<ChartAnalysisResult | null>(null);
  const [chartLoading, setChartLoading] = useState(false);
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const tvWidgetRef = useRef<any>(null);

  // ─── Data Fetching ───────────────────────────────────

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [h, r, n, e, a, tb, l, b, ev, sec, t100] = await Promise.all([
        kazeService.getHealth().catch(() => null),
        kazeService.getReality(),
        kazeService.getNarratives(),
        kazeService.getEdges(),
        kazeService.getAlerts(),
        kazeService.getTomiBridge(),
        kazeService.getLearning(),
        kazeService.getBriefing(),
        kazeService.getEvents(),
        kazeService.getSectors().catch(() => ({ sectors: [], rotation_signals: [] })),
        kazeService.getTop100().catch(() => []),
      ]);
      if (h) setHealth(h);
      setReality(r);
      setNarratives(n);
      setEdges(e);
      setAlerts(a);
      if (tb) setTomiBridge(tb);
      setLearning(l);
      setBriefing(b);
      setEvents(ev);
      setSectors(sec.sectors);
      setRotationSignals(sec.rotation_signals);
      setTop100(t100);
    } catch {
      setError('Failed to load Kaze intelligence data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
    const interval = setInterval(fetchAll, 60000); // refresh every 60s
    return () => clearInterval(interval);
  }, [fetchAll]);

  const handlePoll = async () => {
    setPolling(true);
    try {
      await kazeService.triggerPoll();
      await fetchAll();
    } catch {
      setError('Poll cycle failed');
    } finally {
      setPolling(false);
    }
  };

  // ─── Screener Fetch ───────────────────────────────────

  const fetchScreener = useCallback(async () => {
    try {
      const params: { sector?: string; k_class?: string; limit?: number } = { limit: 50 };
      if (screenerSectorFilter) params.sector = screenerSectorFilter;
      if (screenerKClassFilter) params.k_class = screenerKClassFilter;
      const result = await kazeService.getScreener(params);
      setScreenerTickers(result.tickers);
    } catch {
      /* silent */
    }
  }, [screenerSectorFilter, screenerKClassFilter]);

  useEffect(() => {
    if (tab === 8) fetchScreener(); // Screener tab
  }, [tab, fetchScreener]);

  // ─── Chart Analysis ───────────────────────────────────

  const handleChartAnalysis = async () => {
    if (!chartTicker.trim()) return;
    setChartLoading(true);
    setError(null);
    setChartAnalysis(null);
    try {
      const result = await kazeService.requestChartAnalysis(chartTicker.trim().toUpperCase());
      if (!result) {
        setError('No chart analysis result returned');
        return;
      }
      if (result.error) {
        setError(`Chart analysis failed: ${result.error}`);
        return;
      }
      setChartAnalysis(result);
    } catch {
      setError('Chart analysis request failed');
    } finally {
      setChartLoading(false);
    }
  };

  // ─── TradingView Equity Chart Init ────────────────────

  useEffect(() => {
    if (tab !== 9) return; // Chart Analysis tab

    let widget: any = null;
    let mounted = true;

    const initChart = async () => {
      if (!chartContainerRef.current) return;

      if (!(window as any).TradingView) {
        try {
          await new Promise<void>((resolve, reject) => {
            const script = document.createElement('script');
            script.src = '/charting_library/charting_library.js';
            script.onload = () => resolve();
            script.onerror = () => reject(new Error('TradingView library not found'));
            document.head.appendChild(script);
          });
        } catch {
          return;
        }
      }

      if (!mounted || !(window as any).TradingView) return;

      if (tvWidgetRef.current) {
        try { tvWidgetRef.current.remove(); } catch { /* noop */ }
        tvWidgetRef.current = null;
      }

      const containerId = `kaze_tv_chart_${Date.now()}`;
      chartContainerRef.current.innerHTML = '';
      const div = document.createElement('div');
      div.id = containerId;
      div.style.cssText = 'width:100%;height:500px;';
      chartContainerRef.current.appendChild(div);

      requestAnimationFrame(() => {
        if (!mounted || !document.getElementById(containerId)) return;

        try {
          widget = new (window as any).TradingView.widget({
            container: containerId,
            datafeed: createEquityDatafeed(),
            library_path: '/charting_library/',
            symbol: `US:${chartTicker.toUpperCase()}`,
            autosize: true,
            interval: 'D',
            timezone: 'America/New_York',
            theme: 'Dark',
            locale: 'en',
            toolbar_bg: '#1a1a2e',
            enable_publishing: false,
            hide_side_toolbar: false,
            allow_symbol_change: false,
            details: true,
            loading_screen: { backgroundColor: '#0d1117' },
            disabled_features: [
              'use_localstorage_for_settings',
              'save_chart_properties_to_local_storage',
              'header_saveload',
              'study_templates',
            ],
            save_load_adapter: {
              getAllCharts: () => Promise.resolve([]),
              removeChart: () => Promise.resolve(),
              saveChart: () => Promise.resolve(),
              getChartContent: () => Promise.resolve(''),
              getAllStudyTemplates: () => Promise.resolve([]),
              removeStudyTemplate: () => Promise.resolve(),
              saveStudyTemplate: () => Promise.resolve(),
              getStudyTemplateContent: () => Promise.resolve(''),
            },
          });

          tvWidgetRef.current = widget;
        } catch (err) {
          console.error('TradingView chart init failed:', err);
        }
      });
    };

    initChart();

    return () => {
      mounted = false;
      if (widget) {
        try { widget.remove(); } catch { /* noop */ }
      }
      tvWidgetRef.current = null;
    };
  }, [tab, chartTicker]);

  // ─── Derived ──────────────────────────────────────────

  const engineUp = health?.status === 'operational';
  const tomiConnected = tomiBridge?.tomi_health?.status === 'ok' || tomiBridge?.tomi_health?.status === 'operational';
  const narrativeList: NarrativeState[] = narratives
    ? Array.isArray(narratives.narratives)
      ? narratives.narratives
      : Object.values(narratives.narratives)
    : [];

  // ─── Render ───────────────────────────────────────────

  if (loading && !health) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Air sx={{ fontSize: 36, color: theme.palette.primary.main }} />
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              風の洞察 Kaze no Dōsatsu
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Market Intelligence Engine &bull; Narrative &amp; Edge Detection
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Chip
            icon={<StatusDot ok={engineUp} />}
            label={engineUp ? 'ONLINE' : 'OFFLINE'}
            variant="outlined"
            color={engineUp ? 'success' : 'error'}
          />
          <Chip
            icon={<StatusDot ok={tomiConnected} />}
            label={tomiConnected ? 'TOMI LINKED' : 'TOMI DOWN'}
            variant="outlined"
            color={tomiConnected ? 'info' : 'default'}
            size="small"
          />
          <Tooltip title="Trigger manual poll cycle">
            <span>
              <IconButton onClick={handlePoll} disabled={polling}>
                {polling ? <CircularProgress size={20} /> : <PlayArrow />}
              </IconButton>
            </span>
          </Tooltip>
          <IconButton onClick={fetchAll} title="Refresh">
            <Refresh />
          </IconButton>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Top Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {/* Engine Health */}
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="overline" color="text.secondary">
                Engine Status
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
                {health?.status?.toUpperCase() || 'UNKNOWN'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Adapters: {health?.adapters_connected ?? 0} &bull; Events: {health?.events_processed ?? 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Macro Regime */}
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="overline" color="text.secondary">
                Macro Regime
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
                {reality?.macro_regime?.state || '—'}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {trendIcon(reality?.macro_regime?.trend_direction || '')}
                <Typography variant="caption" color="text.secondary">
                  Confidence: {reality?.macro_regime?.confidence
                    ? `${(reality.macro_regime.confidence * 100).toFixed(0)}%`
                    : '—'}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Crypto Regime */}
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="overline" color="text.secondary">
                Crypto Regime
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
                {reality?.crypto_regime?.state || '—'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Driver: {reality?.crypto_regime?.narrative_driver || '—'} &bull;
                Crowding: {reality?.crypto_regime?.crowding_level?.toFixed(2) ?? '—'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Risk Posture */}
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="overline" color="text.secondary">
                Risk Posture
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
                {reality?.risk_posture?.toUpperCase() || '—'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                VIX: {reality?.volatility_state?.vix_level ?? '—'} &bull;
                Liquidity: {reality?.liquidity_state || '—'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tomi Bridge Mini-Card */}
      {tomiBridge && tomiBridge.total_forwarded > 0 && (
        <Alert
          severity="info"
          icon={<Hub />}
          sx={{ mb: 3 }}
          action={
            <Typography variant="body2" sx={{ fontWeight: 600, pr: 2 }}>
              {tomiBridge.total_forwarded} signals forwarded
            </Typography>
          }
        >
          Kaze → Tomi bridge active. Last signal:{' '}
          {tomiBridge.signals_forwarded.length > 0
            ? `${tomiBridge.signals_forwarded[0].kaze_class} — Tomi action: ${tomiBridge.signals_forwarded[0].tomi_action}`
            : 'none'}
        </Alert>
      )}

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={tab}
          onChange={(_e, v) => setTab(v)}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab icon={<Radar />} iconPosition="start" label="Narratives" />
          <Tab icon={<Article />} iconPosition="start" label="Intel Feed" />
          <Tab icon={<TrendingUp />} iconPosition="start" label="Edge Candidates" />
          <Tab icon={<NotificationsActive />} iconPosition="start" label="Alerts" />
          <Tab icon={<Article />} iconPosition="start" label="Briefing" />
          <Tab icon={<Hub />} iconPosition="start" label="Tomi Bridge" />
          <Tab icon={<Forum />} iconPosition="start" label="富↔風 Chat" />
          <Tab icon={<TableChart />} iconPosition="start" label="Sectors" />
          <Tab icon={<Assessment />} iconPosition="start" label="Screener" />
          <Tab icon={<ShowChart />} iconPosition="start" label="Chart Analysis" />
        </Tabs>
      </Paper>

      {/* ─── Tab: Narratives ────────────────────────────── */}
      <TabPanel value={tab} index={0}>
        {narrativeList.length === 0 ? (
          <Alert severity="info">No narratives detected yet — run a poll cycle</Alert>
        ) : (
          <>
            <Grid container spacing={2}>
              {narrativeList
                .sort((a, b) => b.strength - a.strength)
                .map((n) => (
                  <Grid item xs={12} sm={6} md={4} key={n.narrative_id ?? n.narrative}>
                    <Card variant="outlined">
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>
                            {n.name ?? n.narrative}
                          </Typography>
                          {trendIcon(n.trend)}
                        </Box>
                        <Box sx={{ mb: 1 }}>
                          <Typography variant="caption" color="text.secondary">
                            Strength
                          </Typography>
                          <LinearProgress
                            variant="determinate"
                            value={n.strength * 100}
                            sx={{
                              height: 8,
                              borderRadius: 4,
                              '& .MuiLinearProgress-bar': {
                                backgroundColor: scoreColor(n.strength),
                              },
                            }}
                          />
                        </Box>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1 }}>
                          {(n.assets ?? n.assets_affected ?? []).slice(0, 5).map((a) => (
                            <Chip key={a} label={a} size="small" variant="outlined" />
                          ))}
                          {(n.assets ?? n.assets_affected ?? []).length > 5 && (
                            <Chip label={`+${(n.assets ?? n.assets_affected ?? []).length - 5}`} size="small" />
                          )}
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          {n.price_confirmation ? '✓ Price confirmed' : '○ Unconfirmed'} &bull;
                          Events: {n.event_count}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
            </Grid>

            {/* Collisions */}
            {narratives?.collisions && narratives.collisions.length > 0 && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                  Narrative Collisions
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {narratives.collisions.map((c, i) => (
                    <Chip
                      key={i}
                      icon={<Warning />}
                      label={`${c.a} ⇄ ${c.b}`}
                      color="warning"
                      variant="outlined"
                    />
                  ))}
                </Box>
              </Box>
            )}
          </>
        )}
      </TabPanel>

      {/* ─── Tab: Intel Feed ────────────────────────────── */}
      <TabPanel value={tab} index={1}>
        {events.length === 0 ? (
          <Alert severity="info">No intelligence events yet — run a poll cycle</Alert>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {events.map((ev) => {
              const classColor =
                ev.classification === 'class_a_breaking' ? '#f44336' :
                ev.classification === 'class_b_catalyst' ? '#ff9800' :
                ev.classification === 'class_c_edge' ? '#2196f3' :
                ev.classification === 'class_d_monitor' ? '#9e9e9e' :
                '#616161';
              const classLabel =
                ev.classification === 'class_a_breaking' ? 'A — BREAKING' :
                ev.classification === 'class_b_catalyst' ? 'B — CATALYST' :
                ev.classification === 'class_c_edge' ? 'C — EDGE' :
                ev.classification === 'class_d_monitor' ? 'D — MONITOR' :
                'E — NOISE';
              return (
                <Card key={ev.event_id} variant="outlined" sx={{ borderLeft: `4px solid ${classColor}` }}>
                  <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                      <Box sx={{ flex: 1, mr: 2 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                          {ev.headline}
                        </Typography>
                        {ev.content && (
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            {ev.content.length > 200 ? `${ev.content.slice(0, 200)}…` : ev.content}
                          </Typography>
                        )}
                      </Box>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5, minWidth: 100 }}>
                        <Chip
                          size="small"
                          label={classLabel}
                          sx={{ fontWeight: 700, color: '#fff', backgroundColor: classColor, fontSize: '0.7rem' }}
                        />
                        <Typography variant="h6" sx={{ fontWeight: 700, color: scoreColor(ev.kaze_score), lineHeight: 1 }}>
                          {ev.kaze_score.toFixed(3)}
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 0.5 }}>
                      {ev.asset_tags.slice(0, 5).map((a) => (
                        <Chip key={a} label={a} size="small" variant="outlined" sx={{ fontSize: '0.7rem', height: 20 }} />
                      ))}
                      {ev.narrative_tags.slice(0, 3).map((t) => (
                        <Chip key={t} label={t} size="small" color="primary" variant="outlined" sx={{ fontSize: '0.7rem', height: 20 }} />
                      ))}
                      <Chip label={ev.source} size="small" variant="filled" sx={{ fontSize: '0.65rem', height: 20, opacity: 0.7 }} />
                      {ev.tomi_escalation && (
                        <Chip icon={<Hub sx={{ fontSize: 12 }} />} label="TOMI" size="small" color="info" sx={{ fontSize: '0.65rem', height: 20 }} />
                      )}
                    </Box>
                    {ev.game_theoretic_read && (
                      <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
                        {ev.game_theoretic_read.advantaged.length > 0 && (
                          <Typography variant="caption" color="success.main">
                            ▲ {ev.game_theoretic_read.advantaged.join(', ')}
                          </Typography>
                        )}
                        {ev.game_theoretic_read.trapped.length > 0 && (
                          <Typography variant="caption" color="error.main">
                            ▼ {ev.game_theoretic_read.trapped.join(', ')}
                          </Typography>
                        )}
                      </Box>
                    )}
                    <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.5 }}>
                      {ev.timestamp ? toEST(ev.timestamp) : ''} · {ev.event_id}
                    </Typography>
                  </CardContent>
                </Card>
              );
            })}
          </Box>
        )}
      </TabPanel>

      {/* ─── Tab: Edge Candidates ───────────────────────── */}
      <TabPanel value={tab} index={2}>
        {edges.length === 0 ? (
          <Alert severity="info">No edge candidates identified yet</Alert>
        ) : (
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Asset</TableCell>
                  <TableCell>Edge Type</TableCell>
                  <TableCell align="right">K Score</TableCell>
                  <TableCell>Narrative</TableCell>
                  <TableCell>Consensus</TableCell>
                  <TableCell>Catalyst Window</TableCell>
                  <TableCell align="right">Confidence</TableCell>
                  <TableCell>Horizon</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {edges
                  .sort((a, b) => b.kaze_score - a.kaze_score)
                  .map((e) => (
                    <TableRow key={e.candidate_id}>
                      <TableCell sx={{ fontWeight: 600 }}>{e.asset}</TableCell>
                      <TableCell>
                        <Chip size="small" label={e.edge_type} variant="outlined" />
                      </TableCell>
                      <TableCell align="right">
                        <Typography
                          sx={{ fontWeight: 700, color: scoreColor(e.kaze_score) }}
                        >
                          {e.kaze_score.toFixed(3)}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {e.narrative_context}
                      </TableCell>
                      <TableCell>{e.consensus_state}</TableCell>
                      <TableCell>{e.catalyst_window}</TableCell>
                      <TableCell align="right">
                        {(e.confidence * 100).toFixed(0)}%
                      </TableCell>
                      <TableCell>{e.monitoring_horizon}</TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </TabPanel>

      {/* ─── Tab: Alerts ────────────────────────────────── */}
      <TabPanel value={tab} index={3}>
        {alerts.length === 0 ? (
          <Alert severity="info">No alerts generated yet</Alert>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {alerts.map((a, i) => {
              const t = tierLabel(a.tier);
              return (
                <Alert
                  key={i}
                  severity={t.color === 'error' ? 'error' : t.color === 'warning' ? 'warning' : 'info'}
                  icon={a.tier === 1 ? <ErrorOutline /> : <NotificationsActive />}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                    <Box>
                      <Chip size="small" label={t.label} color={t.color} sx={{ mr: 1 }} />
                      <Typography component="span" variant="body2">
                        {a.summary}
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap', ml: 2 }}>
                      {toEST(a.timestamp)}
                    </Typography>
                  </Box>
                </Alert>
              );
            })}
          </Box>
        )}
      </TabPanel>

      {/* ─── Tab: Briefing ──────────────────────────────── */}
      <TabPanel value={tab} index={4}>
        {!briefing ? (
          <Alert severity="info">No briefing available — run a poll cycle first</Alert>
        ) : (
          <Paper sx={{ p: 3 }}>
            <Typography
              variant="body1"
              sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.8, fontFamily: 'inherit' }}
            >
              {briefing}
            </Typography>
          </Paper>
        )}
      </TabPanel>

      {/* ─── Tab: Tomi Bridge ───────────────────────────── */}
      <TabPanel value={tab} index={5}>
        <Grid container spacing={3}>
          {/* Bridge Status Card */}
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="overline" color="text.secondary">
                  Tomi Connection
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                  <StatusDot ok={tomiConnected} />
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {tomiConnected ? 'Connected' : 'Disconnected'}
                  </Typography>
                </Box>
                <Divider sx={{ my: 1.5 }} />
                <Typography variant="caption" color="text.secondary">
                  Total signals forwarded
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700 }}>
                  {tomiBridge?.total_forwarded ?? 0}
                </Typography>
              </CardContent>
            </Card>

            {/* Learning Stats */}
            {learning && (
              <Card sx={{ mt: 2 }}>
                <CardContent>
                  <Typography variant="overline" color="text.secondary">
                    Learning Stats
                  </Typography>
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="body2">
                      Hit Rate: <strong>{typeof learning.edge_hit_rate === 'number' ? `${(learning.edge_hit_rate * 100).toFixed(1)}%` : learning.edge_hit_rate}</strong>
                    </Typography>
                    <Typography variant="body2">
                      False Positive Rate: <strong>{typeof learning.false_positive_rate === 'number' ? `${(learning.false_positive_rate * 100).toFixed(1)}%` : learning.false_positive_rate}</strong>
                    </Typography>
                    <Typography variant="body2">
                      Total Feedback: <strong>{learning.total_feedback_records}</strong>
                    </Typography>
                    <Typography variant="body2">
                      Edges Realized: <strong>{learning.edge_realized_count}</strong>
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            )}
          </Grid>

          {/* Signal Log */}
          <Grid item xs={12} md={8}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
              Forwarded Signal Log
            </Typography>
            {!tomiBridge?.signals_forwarded?.length ? (
              <Alert severity="info">No signals forwarded to Tomi yet</Alert>
            ) : (
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Time</TableCell>
                      <TableCell>Assets</TableCell>
                      <TableCell>Kaze Class</TableCell>
                      <TableCell align="right">K Score</TableCell>
                      <TableCell>Tomi Action</TableCell>
                      <TableCell align="right">Dominion</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {tomiBridge.signals_forwarded.map((s, i) => (
                      <TableRow key={i}>
                        <TableCell sx={{ fontSize: '0.8rem' }}>
                          {toEST(s.timestamp)}
                        </TableCell>
                        <TableCell>
                          {s.assets.slice(0, 3).map((a) => (
                            <Chip key={a} label={a} size="small" sx={{ mr: 0.5 }} />
                          ))}
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={s.kaze_class}
                            color={s.kaze_class === 'A' ? 'error' : s.kaze_class === 'B' ? 'warning' : 'default'}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600, color: scoreColor(s.kaze_score) }}>
                          {s.kaze_score.toFixed(3)}
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={s.tomi_action}
                            icon={
                              s.tomi_action === 'EXECUTE' ? <CheckCircle /> :
                              s.tomi_action === 'REJECT' ? <ErrorOutline /> :
                              undefined
                            }
                            color={
                              s.tomi_action === 'EXECUTE' ? 'success' :
                              s.tomi_action === 'REJECT' ? 'error' :
                              'default'
                            }
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>
                          {s.tomi_dominion_score.toFixed(2)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Grid>
        </Grid>
      </TabPanel>

      {/* Tab: Agent Chat */}
      <TabPanel value={tab} index={6}>
        <AgentChatPanel
          perspective="kaze"
          getChat={() => kazeService.getChat()}
          sendChat={(msg) => kazeService.sendChat(msg)}
          triggerExchange={(init) => kazeService.triggerChatExchange(init)}
          clearChat={() => kazeService.clearChat()}
        />
      </TabPanel>

      {/* ─── Tab: Sectors ───────────────────────────────── */}
      <TabPanel value={tab} index={7}>
        {sectors.length === 0 ? (
          <Alert severity="info">No sector data yet — run a poll cycle to populate DW + Yahoo analysis</Alert>
        ) : (
          <>
            {/* Rotation Signals */}
            {rotationSignals.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                  Rotation Signals
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {rotationSignals.map((rs, i) => (
                    <Chip
                      key={i}
                      icon={<TrendingUp />}
                      label={rs.description}
                      color="info"
                      variant="outlined"
                    />
                  ))}
                </Box>
              </Box>
            )}

            {/* Sector Consensus Table */}
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Rank</TableCell>
                    <TableCell>Sector</TableCell>
                    <TableCell>ETF</TableCell>
                    <TableCell align="right">RS Score</TableCell>
                    <TableCell align="right">Trend</TableCell>
                    <TableCell align="right">Momentum</TableCell>
                    <TableCell>Consensus</TableCell>
                    <TableCell align="right">Breadth %</TableCell>
                    <TableCell align="right">Leaders</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sectors.map((s, i) => {
                    const consensusColor =
                      s.consensus_label === 'STRONG_BUY' ? '#4caf50' :
                      s.consensus_label === 'BUY' ? '#8bc34a' :
                      s.consensus_label === 'NEUTRAL' ? '#ff9800' :
                      s.consensus_label === 'SELL' ? '#ff5722' : '#f44336';
                    return (
                      <TableRow key={s.sector}>
                        <TableCell sx={{ fontWeight: 600 }}>#{i + 1}</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>{s.sector}</TableCell>
                        <TableCell>{s.etf}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600, color: scoreColor(s.dw_composite || 0) }}>
                          {s.dw_composite?.toFixed(3)}
                        </TableCell>
                        <TableCell align="right">
                          {s.trend_score?.toFixed(2)}
                        </TableCell>
                        <TableCell align="right">
                          {s.momentum_score?.toFixed(2)}
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={s.consensus_label}
                            sx={{ fontWeight: 700, color: '#fff', backgroundColor: consensusColor, fontSize: '0.7rem' }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          {s.breadth_score != null ? `${(s.breadth_score * 100).toFixed(0)}%` : '—'}
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption">{s.signal || '—'}</Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </TabPanel>

      {/* ─── Tab: Screener ──────────────────────────────── */}
      <TabPanel value={tab} index={8}>
        <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'center' }}>
          <TextField
            select
            label="Sector"
            value={screenerSectorFilter}
            onChange={(e) => setScreenerSectorFilter(e.target.value)}
            size="small"
            sx={{ minWidth: 180 }}
          >
            <MenuItem value="">All Sectors</MenuItem>
            {['Technology', 'Healthcare', 'Financials', 'Energy', 'Industrials',
              'Consumer Discretionary', 'Consumer Staples', 'Utilities',
              'Real Estate', 'Materials', 'Communication Services'].map((s) => (
              <MenuItem key={s} value={s}>{s}</MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="K-Class"
            value={screenerKClassFilter}
            onChange={(e) => setScreenerKClassFilter(e.target.value)}
            size="small"
            sx={{ minWidth: 140 }}
          >
            <MenuItem value="">All Classes</MenuItem>
            <MenuItem value="ALPHA">ALPHA</MenuItem>
            <MenuItem value="STRONG">STRONG</MenuItem>
            <MenuItem value="NEUTRAL">NEUTRAL</MenuItem>
            <MenuItem value="WEAK">WEAK</MenuItem>
            <MenuItem value="AVOID">AVOID</MenuItem>
          </TextField>
          <Button variant="outlined" size="small" onClick={fetchScreener}>
            Apply
          </Button>
          <Typography variant="caption" color="text.secondary">
            {screenerTickers.length} tickers
          </Typography>
        </Box>

        {/* Top 100 mini section */}
        {top100.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Yahoo Finance Top Movers
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              {top100.slice(0, 20).map((t) => (
                <Chip
                  key={t.symbol}
                  label={`${t.symbol} ${t.change_pct >= 0 ? '+' : ''}${t.change_pct.toFixed(1)}%`}
                  size="small"
                  color={t.change_pct >= 0 ? 'success' : 'error'}
                  variant="outlined"
                  sx={{ fontSize: '0.7rem' }}
                />
              ))}
            </Box>
          </Box>
        )}

        {screenerTickers.length === 0 ? (
          <Alert severity="info">No screener data — run a poll cycle to populate sector analysis</Alert>
        ) : (
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Ticker</TableCell>
                  <TableCell>Sector</TableCell>
                  <TableCell align="right">K-Score</TableCell>
                  <TableCell>K-Class</TableCell>
                  <TableCell align="right">RS</TableCell>
                  <TableCell align="right">Entry $</TableCell>
                  <TableCell align="right">Target $</TableCell>
                  <TableCell align="right">Stop $</TableCell>
                  <TableCell align="right">R:R</TableCell>
                  <TableCell>P&F Signal</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {screenerTickers.map((t) => {
                  const kColor =
                    t.k_class === 'ALPHA' ? '#4caf50' :
                    t.k_class === 'STRONG' ? '#8bc34a' :
                    t.k_class === 'NEUTRAL' ? '#ff9800' :
                    t.k_class === 'WEAK' ? '#ff5722' : '#f44336';
                  return (
                    <TableRow key={t.ticker} hover sx={{ cursor: 'pointer' }} onClick={() => { setChartTicker(t.ticker); setTab(9); }}>
                      <TableCell sx={{ fontWeight: 700 }}>{t.ticker}</TableCell>
                      <TableCell>{t.sector}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: kColor }}>
                        {t.k_score?.toFixed(3)}
                      </TableCell>
                      <TableCell>
                        <Chip size="small" label={t.k_class} sx={{ fontWeight: 700, color: '#fff', backgroundColor: kColor, fontSize: '0.65rem' }} />
                      </TableCell>
                      <TableCell align="right">{t.components?.technical_rs?.toFixed(3) || '—'}</TableCell>
                      <TableCell align="right">${t.entry_price?.entry?.toFixed(2) || '—'}</TableCell>
                      <TableCell align="right" sx={{ color: '#4caf50' }}>${t.entry_price?.target?.toFixed(2) || '—'}</TableCell>
                      <TableCell align="right" sx={{ color: '#f44336' }}>${t.entry_price?.stop?.toFixed(2) || '—'}</TableCell>
                      <TableCell align="right">{t.entry_price?.risk_reward?.toFixed(1) || '—'}:1</TableCell>
                      <TableCell>
                        {t.pnf_signal ? (
                          <Chip
                            size="small"
                            label={t.pnf_signal}
                            color={t.pnf_signal.toLowerCase() === 'buy' ? 'success' : t.pnf_signal.toLowerCase() === 'sell' ? 'error' : 'default'}
                            variant="outlined"
                            sx={{ fontSize: '0.65rem', height: 20 }}
                          />
                        ) : '—'}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </TabPanel>

      {/* ─── Tab: Chart Analysis ────────────────────────── */}
      <TabPanel value={tab} index={9}>
        <Grid container spacing={3}>
          {/* Chart Controls + TradingView */}
          <Grid item xs={12} md={8}>
            <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
              <TextField
                label="Ticker"
                value={chartTicker}
                onChange={(e) => setChartTicker(e.target.value.toUpperCase())}
                size="small"
                sx={{ width: 120 }}
              />
              <Button
                variant="contained"
                onClick={handleChartAnalysis}
                disabled={chartLoading || !chartTicker.trim()}
                startIcon={chartLoading ? <CircularProgress size={16} /> : <ShowChart />}
              >
                {chartLoading ? 'Analyzing...' : 'Tomi Analyze'}
              </Button>
              <Typography variant="caption" color="text.secondary">
                Tomi runs 8-phase agentic analysis: Regime → Sentiment → LMSR → Pattern → GameTheory → Expectancy → Discipline → Composite
              </Typography>
            </Box>

            {/* TradingView Chart Container */}
            <Paper sx={{ mb: 2, overflow: 'hidden', borderRadius: 1 }}>
              <div ref={chartContainerRef} style={{ width: '100%', height: 500 }} />
            </Paper>
          </Grid>

          {/* Tomi Analysis Results Panel */}
          <Grid item xs={12} md={4}>
            {chartAnalysis ? (
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      {chartAnalysis.ticker}
                    </Typography>
                    <Chip
                      label={chartAnalysis.action}
                      color={
                        chartAnalysis.action === 'EXECUTE' ? 'success' :
                        chartAnalysis.action === 'REJECT' ? 'error' :
                        'warning'
                      }
                      sx={{ fontWeight: 700 }}
                    />
                  </Box>

                  <Divider sx={{ mb: 2 }} />

                  {/* Dominion Score */}
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="overline" color="text.secondary">
                      Dominion Score
                    </Typography>
                    <Typography variant="h3" sx={{ fontWeight: 700, color: scoreColor(chartAnalysis.dominion_score) }}>
                      {chartAnalysis.dominion_score?.toFixed(3)}
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={(chartAnalysis.dominion_score || 0) * 100}
                      sx={{
                        height: 8, borderRadius: 4, mt: 0.5,
                        '& .MuiLinearProgress-bar': { backgroundColor: scoreColor(chartAnalysis.dominion_score || 0) },
                      }}
                    />
                  </Box>

                  {/* K-Score & Entry */}
                  {chartAnalysis.k_score != null && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="overline" color="text.secondary">K-Score</Typography>
                      <Typography variant="h5" sx={{ fontWeight: 700 }}>
                        {chartAnalysis.k_score.toFixed(3)} — {chartAnalysis.sector_consensus || '—'}
                      </Typography>
                    </Box>
                  )}

                  {chartAnalysis.entry_price != null && (
                    <Grid container spacing={1} sx={{ mb: 2 }}>
                      <Grid item xs={4}>
                        <Typography variant="overline" color="text.secondary" sx={{ fontSize: '0.6rem' }}>Entry</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 700 }}>${chartAnalysis.entry_price.toFixed(2)}</Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography variant="overline" color="text.secondary" sx={{ fontSize: '0.6rem' }}>Target</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 700, color: '#4caf50' }}>${chartAnalysis.target?.toFixed(2) || '—'}</Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography variant="overline" color="text.secondary" sx={{ fontSize: '0.6rem' }}>Stop</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 700, color: '#f44336' }}>${chartAnalysis.stop_loss?.toFixed(2) || '—'}</Typography>
                      </Grid>
                    </Grid>
                  )}

                  {/* Phase Scores */}
                  {chartAnalysis.components && Object.keys(chartAnalysis.components).length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="overline" color="text.secondary">Phase Breakdown</Typography>
                      {Object.entries(chartAnalysis.components).map(([phase, score]) => (
                        <Box key={phase} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                          <Typography variant="caption" sx={{ textTransform: 'capitalize' }}>
                            {phase.replace(/_/g, ' ')}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '60%' }}>
                            <LinearProgress
                              variant="determinate"
                              value={(typeof score === 'number' ? score : 0) * 100}
                              sx={{
                                flex: 1, height: 6, borderRadius: 3,
                                '& .MuiLinearProgress-bar': { backgroundColor: scoreColor(typeof score === 'number' ? score : 0) },
                              }}
                            />
                            <Typography variant="caption" sx={{ fontWeight: 600, minWidth: 35, textAlign: 'right' }}>
                              {(typeof score === 'number' ? score : 0).toFixed(2)}
                            </Typography>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  )}

                  {/* Indicators */}
                  {chartAnalysis.chart_context && (
                    <Box>
                      <Typography variant="overline" color="text.secondary">Indicators</Typography>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {chartAnalysis.chart_context.rsi != null && (
                          <Chip size="small" label={`RSI ${chartAnalysis.chart_context.rsi.toFixed(1)}`} variant="outlined" />
                        )}
                        {chartAnalysis.chart_context.trend && (
                          <Chip
                            size="small"
                            label={chartAnalysis.chart_context.trend}
                            color={
                              chartAnalysis.chart_context.trend.toLowerCase().includes('up')
                                ? 'success'
                                : chartAnalysis.chart_context.trend.toLowerCase().includes('down')
                                  ? 'error'
                                  : 'default'
                            }
                            variant="outlined"
                          />
                        )}
                        {chartAnalysis.chart_context.macd_signal && (
                          <Chip size="small" label={`MACD ${chartAnalysis.chart_context.macd_signal}`} variant="outlined" />
                        )}
                      </Box>
                    </Box>
                  )}

                  <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 2 }}>
                    {chartAnalysis.timestamp ? toEST(chartAnalysis.timestamp) : ''}
                  </Typography>
                </CardContent>
              </Card>
            ) : (
              <Card sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 300 }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <ShowChart sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                  <Typography variant="body1" color="text.secondary">
                    Enter a ticker and click "Tomi Analyze"
                  </Typography>
                  <Typography variant="caption" color="text.disabled">
                    Tomi will run a full 8-phase dominion evaluation
                  </Typography>
                </CardContent>
              </Card>
            )}
          </Grid>
        </Grid>
      </TabPanel>
    </Box>
  );
}
